/**
 * PropertySection 共通ユーティリティ (新スキーマ対応)
 *
 * 契約情報は section.contract に集約。専有部固有のフィールド (floor, floor_plan,
 * rental_area_m2 等) は section.details に格納される。
 */
import dayjs from "dayjs";
import { AREA_TYPE_LABELS } from "@ippon/shared-types";
import {
  calculateTotalTsuboUnitPrice,
  calculateWeightedTsuboUnitPrice,
} from "@ippon/shared-utils";
import type {
  PropertySection,
  ContractorDetail,
  PrivateAreaRow,
  AncillaryRow,
  RentRollKpi,
  OccupancyStatus,
  SectionDetails,
} from "@/types/propertySection";

// ---------------------------------------------------------------------------
// 配列正規化
// ---------------------------------------------------------------------------
function normalizeArray<T>(value: T | T[] | undefined | null): T[] {
  if (Array.isArray(value)) return value;
  if (value != null) return [value];
  return [];
}

// ---------------------------------------------------------------------------
// details の安全アクセス
// ---------------------------------------------------------------------------
function getDetail<T = unknown>(
  details: SectionDetails | undefined,
  key: string,
): T | undefined {
  if (!details) return undefined;
  return details[key] as T | undefined;
}

function getDetailString(
  details: SectionDetails | undefined,
  key: string,
): string {
  const v = getDetail(details, key);
  if (v == null) return "";
  return typeof v === "string" ? v : String(v);
}

function getDetailNumber(
  details: SectionDetails | undefined,
  key: string,
): number {
  const v = getDetail(details, key);
  if (v == null || v === "") return 0;
  return Number(v) || 0;
}

// ---------------------------------------------------------------------------
// 最新契約の取得
// ---------------------------------------------------------------------------
export function getLatestContract(
  section: PropertySection,
): ContractorDetail | null {
  const contracts = normalizeArray(section.contract?.contractor_details);
  if (contracts.length === 0) return null;

  const sorted = [...contracts].sort(
    (a, b) =>
      new Date(b.created_at ?? "1970-01-01").getTime() -
      new Date(a.created_at ?? "1970-01-01").getTime(),
  );
  return sorted[0];
}

// ---------------------------------------------------------------------------
// 契約者名の取得
// ---------------------------------------------------------------------------
export function getContractorName(
  section: PropertySection,
  contract: ContractorDetail | null,
): string {
  if (!contract) return "";

  if (contract.contractor_type === "corporate") {
    const corpFromContract = normalizeArray(contract.corporate_details);
    if (corpFromContract.length > 0) {
      return corpFromContract[corpFromContract.length - 1].company_name || "";
    }
    const corpFromSection = normalizeArray(section.contract?.corporate_details);
    if (corpFromSection.length > 0) {
      return corpFromSection[corpFromSection.length - 1].company_name || "";
    }
    return "";
  }

  // individual
  const indFromContract = normalizeArray(contract.individual_details);
  if (indFromContract.length > 0) {
    const ind = indFromContract[indFromContract.length - 1];
    return `${ind.individual_last_name || ""}${ind.individual_first_name || ""}`;
  }
  const indFromSection = normalizeArray(section.contract?.individual_details);
  if (indFromSection.length > 0) {
    const ind = indFromSection[indFromSection.length - 1];
    return `${ind.individual_last_name || ""}${ind.individual_first_name || ""}`;
  }
  return "";
}

// ---------------------------------------------------------------------------
// 日付・金額フォーマット
// ---------------------------------------------------------------------------
export function formatSectionDate(d: string | undefined | null): string {
  return d ? dayjs(d).format("YYYY/MM/DD") : "";
}

export function formatCurrency(n: number): string {
  return n > 0 ? `¥${n.toLocaleString("ja-JP")}` : "-";
}

// ---------------------------------------------------------------------------
// occupancy_status
// ---------------------------------------------------------------------------
export function safeOccupancyStatus(
  raw: string | undefined | null,
): OccupancyStatus {
  if (raw === "occupied" || raw === "vacant" || raw === "out_of_service")
    return raw;
  return "vacant";
}

// ---------------------------------------------------------------------------
// PropertySection → PrivateAreaRow (専有部 / 貸室向け)
// ---------------------------------------------------------------------------
export function toPrivateAreaRow(section: PropertySection): PrivateAreaRow {
  const latest = getLatestContract(section);
  const status = safeOccupancyStatus(section.occupancy_status);
  const isVacant = status === "vacant";
  const details = section.details;

  const areaM2 = getDetailNumber(details, "rental_area_m2");
  const areaTsubo = getDetailNumber(details, "area_tsubo");
  const floor = getDetailString(details, "floor");
  const floorPlan = getDetailString(details, "floor_plan");
  const usage = section.section_usage || "";

  const contract = section.contract;
  const maintenanceFee = isVacant
    ? 0
    : Number(latest?.maintenance_fee ?? contract?.maintenance_fee ?? 0);

  return {
    id: section.property_section_id,
    name: section.landmark_name || "",
    floor,
    floorPlan,
    area: areaM2 > 0 ? `${areaM2}㎡` : "",
    usage,
    contractForm: isVacant ? "" : latest?.contract_form || "",
    occupancyStatus: status,
    contractorName: isVacant ? "" : getContractorName(section, latest),
    monthlyRent: isVacant ? 0 : Number(latest?.monthly_rent ?? 0),
    areaTsubo,
    rentUnitPrice: isVacant
      ? 0
      : Number(latest?.rent_unit_price ?? contract?.rent_per_tsubo ?? 0),
    maintenanceFee,
    maintenanceFeeUnitPrice: isVacant
      ? 0
      : Number(
          latest?.maintenance_fee_unit_price ??
            contract?.maintenance_fee_per_tsubo ??
            0,
        ),
    totalUnitPrice: isVacant
      ? 0
      : (calculateTotalTsuboUnitPrice(
          Number(latest?.rent_unit_price ?? contract?.rent_per_tsubo ?? 0),
          Number(
            latest?.maintenance_fee_unit_price ??
              contract?.maintenance_fee_per_tsubo ??
              0,
          ),
        ) ?? 0),
    securityDeposit: isVacant ? 0 : Number(latest?.security_deposit ?? 0),
    keyMoney: isVacant ? 0 : Number(latest?.key_money ?? 0),
    contractStartDate: isVacant
      ? ""
      : formatSectionDate(latest?.contract_start_date),
    contractEndDate: isVacant
      ? ""
      : formatSectionDate(latest?.contract_end_date),
  };
}

// ---------------------------------------------------------------------------
// EquipmentRecord (revenue_type=ancillary_income) → AncillaryRow
// レントロールで PropertySection と Equipment を統合表示する用
// ---------------------------------------------------------------------------
export function equipmentToAncillaryRow(eq: {
  equipment_id: string;
  name: string;
  location?: string;
  category?: string;
  revenue_status?: string;
  monthly_income?: number;
  contractor_name?: string;
  contract_start_date?: string;
  contract_end_date?: string;
}): AncillaryRow {
  // 設備の revenue_status を occupancy 概念にマップ
  const status: OccupancyStatus =
    eq.revenue_status === "active"
      ? "occupied"
      : eq.revenue_status === "suspended"
        ? "out_of_service"
        : "vacant";
  return {
    id: `equipment:${eq.equipment_id}`, // 衝突回避のため prefix
    name: eq.name || "",
    sectionCategory: AREA_TYPE_LABELS.facility,
    sectionUsage: eq.category || "",
    occupancyStatus: status,
    contractorName: eq.contractor_name || "",
    monthlyIncome: Number(eq.monthly_income || 0),
    pricingType: "fixed",
    unitPrice: 0,
    quantity: 0,
    quantityUnit: "",
    securityDeposit: 0,
    keyMoney: 0,
    contractStartDate: formatSectionDate(eq.contract_start_date),
    contractEndDate: formatSectionDate(eq.contract_end_date),
    remarks: eq.location || "",
  };
}

// ---------------------------------------------------------------------------
// PropertySection → AncillaryRow (revenue_type=ancillary_income 向け)
// ---------------------------------------------------------------------------
export function toAncillaryRow(section: PropertySection): AncillaryRow {
  const latest = getLatestContract(section);
  const status = safeOccupancyStatus(section.occupancy_status);
  const isVacant = status === "vacant";

  return {
    id: section.property_section_id,
    name: section.landmark_name || "",
    sectionCategory: section.area_type
      ? AREA_TYPE_LABELS[section.area_type]
      : section.section_category || "",
    sectionUsage: section.section_usage || "",
    occupancyStatus: status,
    contractorName: isVacant ? "" : getContractorName(section, latest),
    monthlyIncome: isVacant ? 0 : Number(latest?.monthly_rent ?? 0),
    pricingType:
      latest?.pricing_type === "unit_quantity" ? "unit_quantity" : "fixed",
    unitPrice: isVacant ? 0 : Number(latest?.unit_price ?? 0),
    quantity: isVacant ? 0 : Number(latest?.quantity ?? 0),
    quantityUnit: isVacant ? "" : String(latest?.quantity_unit ?? ""),
    securityDeposit: isVacant ? 0 : Number(latest?.security_deposit ?? 0),
    keyMoney: isVacant ? 0 : Number(latest?.key_money ?? 0),
    contractStartDate: isVacant
      ? ""
      : formatSectionDate(latest?.contract_start_date),
    contractEndDate: isVacant
      ? ""
      : formatSectionDate(latest?.contract_end_date),
    remarks: isVacant ? "" : latest?.contract_remarks || "",
  };
}

// ---------------------------------------------------------------------------
// KPI 計算
// ---------------------------------------------------------------------------
export function calcRentRollKpi(
  privateAreas: PrivateAreaRow[],
  ancillaries: AncillaryRow[],
): RentRollKpi {
  const occupiedPA = privateAreas.filter(
    (r) => r.occupancyStatus === "occupied",
  );
  const totalRent = occupiedPA.reduce((s, r) => s + r.monthlyRent, 0);
  const totalMaintenanceFee = occupiedPA.reduce(
    (s, r) => s + r.maintenanceFee,
    0,
  );

  const occupancyRate =
    privateAreas.length > 0
      ? ((occupiedPA.length / privateAreas.length) * 100).toFixed(1)
      : "0.0";
  const vacantCount = privateAreas.filter(
    (r) => r.occupancyStatus === "vacant",
  ).length;

  const activeAn = ancillaries.filter((r) => r.occupancyStatus === "occupied");
  const totalAncillary = activeAn.reduce((s, r) => s + r.monthlyIncome, 0);
  const inactiveAnCount = ancillaries.filter(
    (r) => r.occupancyStatus === "vacant",
  ).length;

  return {
    totalRent,
    totalMaintenanceFee,
    occupancyRate,
    vacantCount,
    totalAncillary,
    inactiveAnCount,
    totalMonthly: totalRent + totalMaintenanceFee + totalAncillary,
    weightedUnitPrice:
      calculateWeightedTsuboUnitPrice(
        occupiedPA.map((row) => ({
          monthlyAmount: row.monthlyRent + row.maintenanceFee,
          areaTsubo: row.areaTsubo,
        })),
      ) ?? 0,
  };
}

// ---------------------------------------------------------------------------
// 公開
// ---------------------------------------------------------------------------
export { normalizeArray };
